<template>
    <div>
        <modal @close_modal="closeModal" v-if="showModal" :title="$t('confusion_matrix.details')">
            <template v-slot:content>
                <confusion-matrix-items
                        :model_id="modalContentModelId"
                        :type="modalContentType"
                        :items="modalContent"
                        @prediction_accepted="onPredictionAccepted"></confusion-matrix-items>
            </template>
        </modal>
      <row v-if="items.length > 0">
        <col3>
        <my-boxes-modal :label_name="boxes_modal_label_name" v-if="show_boxes_modal" @close_modal="close_preview_label_in_modal"></my-boxes-modal>
        </col3>
        <col3>
        <v-slider
            :label="$t('confusion_matrix.confidence_treshold')"
            :hint="$t('confusion_matrix.confidence_treshold_hint')"
            max="100"
            min="0"
            step="1"
            thumb-label
            v-model="confidenceThreshold"
        ></v-slider>
        </col3>
        <col3>
          <v-slider
              :label="'Confused threshold'"
              hint="What model score makes the predictions confused?"
              max="100"
              min="0"
              step="1"
              v-model="confusedThreshold"
              thumb-label
          ></v-slider>
        </col3>
        <col3>
          <v-slider
              :label="'Background threshold'"
              hint="What model score makes the prediction false positive?"
              max="100"
              min="0"
              step="1"
              v-model="backgroundThreshold"
              thumb-label
          ></v-slider>
        </col3>
        <col3 v-if="showFitColumns">
          <v-slider
              :label="'Fit threshold'"
              hint="IoU percent that counts as a super-accurate box"
              max="100"
              min="45"
              step="1"
              v-model="fitThreshold"
              thumb-label
          ></v-slider>
        </col3>
        <col3 v-if="showFitColumns">
          <v-slider
              :label="'Point distance'"
              hint="Normalized distance that counts as a really close point"
              max="0.1"
              min="0"
              step="0.005"
              v-model="pointDistance"
              thumb-label
          ></v-slider>
        </col3>
      </row>
        <data-table
                :show_delete_action="false"
                :show_edit_action="false"
                :items_per_page="50"
                :items="items"
                :single_expand="true"
                :template_slots="[
                  'label', 'correct1', 'correct2',
                  'correct_tight1', 'correct_tight2',
                  'correct_loose1', 'correct_loose2',
                  'correct_low1', 'correct_low2',
                  'confused_with1', 'confused_with2',
                  'confused_with_low1', 'confused_with_low2',
                  'confused_with_background1', 'confused_with_background2',
                  'confused_with_background_low1', 'confused_with_background_low2',
                  'missed1', 'missed2']"
                :headers="headers">
            <template v-slot:label="{ item }">
                <v-chip light>
                    {{ item.label }}
                    <i class="fas fa-table-cells-large small ml-2" @click="preview_label_in_modal(item.label)"></i>
                </v-chip>
            </template>
            <template v-slot:correct1="{item}">
                <span v-if="item.correct1 == null">—</span>
                <bar-chart
                    v-else
                    @click="openModal(item.correct1_items, 'correct', 1)"
                    :max_value="item.total1"
                    :absolutes="false"
                    :inverse_colors="false"
                    :values="[item.correct1]"
                ></bar-chart>
            </template>
            <template v-slot:correct2="{item}">
                <span v-if="item.correct2 == null">—</span>
                <bar-chart
                        v-else
                        @click="openModal(item.correct2_items, 'correct', 2)"
                        :max_value="item.total2"
                        :absolutes="false"
                        :inverse_colors="false"
                        :values="[item.correct2]"
                ></bar-chart>
            </template>
            <template v-slot:correct_tight1="{item}">
                <span v-if="item.correct_tight1 == null">—</span>
                <bar-chart
                    v-else
                    @click="openModal(item.correct_tight1_items, 'correct', 1)"
                    :max_value="item.total1"
                    :absolutes="false"
                    :inverse_colors="false"
                    :values="[item.correct_tight1]"
                ></bar-chart>
            </template>
            <template v-slot:correct_tight2="{item}">
                <span v-if="item.correct_tight2 == null">—</span>
                <bar-chart
                    v-else
                    @click="openModal(item.correct_tight2_items, 'correct', 2)"
                    :max_value="item.total2"
                    :absolutes="false"
                    :inverse_colors="false"
                    :values="[item.correct_tight2]"
                ></bar-chart>
            </template>
            <template v-slot:correct_loose1="{item}">
                <span v-if="item.correct_loose1 == null">—</span>
                <bar-chart
                    v-else
                    @click="openModal(item.correct_loose1_items, 'correct', 1)"
                    :max_value="item.total1"
                    :absolutes="false"
                    :inverse_colors="false"
                    :values="[item.correct_loose1]"
                ></bar-chart>
            </template>
            <template v-slot:correct_loose2="{item}">
                <span v-if="item.correct_loose2 == null">—</span>
                <bar-chart
                    v-else
                    @click="openModal(item.correct_loose2_items, 'correct', 2)"
                    :max_value="item.total2"
                    :absolutes="false"
                    :inverse_colors="false"
                    :values="[item.correct_loose2]"
                ></bar-chart>
            </template>
            <template v-slot:correct_low1="{item}">
              <bar-chart
                  @click="openModal(item.correct_low1_items, 'correct', 1)"
                  :max_value="item.total1"
                  :absolutes="false"
                  :inverse_colors="true"
                  :values="[item.correct_low1]"
              ></bar-chart>
            </template>
            <template v-slot:correct_low2="{item}">
              <bar-chart
                  @click="openModal(item.correct_low2_items, 'correct', 2)"
                  :max_value="item.total_low2"
                  :absolutes="false"
                  :inverse_colors="true"
                  :values="[item.correct_low2]"
              ></bar-chart>
            </template>
            <template v-slot:confused_with1="{item}">
                <bar-chart
                        @click="openModal(item.confused_with1_items, 'confused_with', 1)"
                        :max_value="item.total1"
                        :absolutes="true"
                        :inverse_colors="true"
                        :values="[item.confused_with1]"
                ></bar-chart>
            </template>
            <template v-slot:confused_with2="{item}">
                <bar-chart
                        @click="openModal(item.confused_with2_items, 'confused_with', 2)"
                        :max_value="item.total2"
                        :absolutes="true"
                        :inverse_colors="true"
                        :values="[item.confused_with2]"
                ></bar-chart>
            </template>
            <template v-slot:confused_with_low1="{item}">
              <bar-chart
                  @click="openModal(item.confused_with_low1_items, 'confused_with', 1)"
                  :max_value="item.total1"
                  :absolutes="true"
                  :inverse_colors="true"
                  :values="[item.confused_with_low1]"
              ></bar-chart>
            </template>
            <template v-slot:confused_with_low2="{item}">

              <bar-chart
                  @click="openModal(item.confused_with_low2_items, 'confused_with', 2)"
                  :max_value="item.total2"
                  :absolutes="true"
                  :inverse_colors="true"
                  :values="[item.confused_with_low2]"
              ></bar-chart>
            </template>
            <template v-slot:missed1="{item}">

                <bar-chart
                        @click="openModal(item.missed1_items, 'missed', 1)"
                        :max_value="item.total1"
                        :absolutes="true"
                        :inverse_colors="true"
                        :values="[item.missed1]"
                ></bar-chart>
            </template>
            <template v-slot:missed2="{item}">
                <bar-chart
                        @click="openModal(item.missed2_items, 'missed', 2)"
                        :max_value="item.total2"
                        :absolutes="true"
                        :inverse_colors="true"
                        :values="[item.missed2]"
                ></bar-chart>
            </template>
            <template v-slot:confused_with_background1="{item}">
                <bar-chart
                        @click="openModal(item.confused_with_background1_items, 'confused_with_background', 1)"
                        :max_value="item.total1"
                        :absolutes="true"
                        :inverse_colors="true"
                        :values="[item.confused_with_background1]"
                ></bar-chart>
            </template>
            <template v-slot:confused_with_background2="{item}">
                <bar-chart
                        @click="openModal(item.confused_with_background2_items, 'confused_with_background', 2)"
                        :max_value="item.total2"
                        :absolutes="true"
                        :inverse_colors="true"
                        :values="[item.confused_with_background2]"
                ></bar-chart>
            </template>
            <template v-slot:confused_with_background_low1="{item}">
              <bar-chart
                  @click="openModal(item.confused_with_background_low1_items, 'confused_with_background_low', 1)"
                  :max_value="item.total1"
                  :absolutes="true"
                  :inverse_colors="true"
                  :values="[item.confused_with_background_low1]"
              ></bar-chart>
            </template>
            <template v-slot:confused_with_background_low2="{item}">
              <bar-chart
                  @click="openModal(item.confused_with_background_low2_items, 'confused_with_background_low', 2)"
                  :max_value="item.total2"
                  :absolutes="true"
                  :inverse_colors="true"
                  :values="[item.confused_with_background_low2]"
              ></bar-chart>
            </template>
        </data-table>
    </div>
</template>

<script>
  import DataTable from "@/components/DataTable";
  import BarChart from "@/components/confusion_matrix/BarChart";
  import Modal from "@/components/Modal";
  import ConfusionMatrixItems from "@/components/confusion_matrix/ConfusionMatrixItems";
  import Row from "@/components/layout/Row";
  import Col3 from "@/components/layout/Col3";
  import MyBoxesModal from "@/components/MyBoxesModal";

  export default {
    name: "ConfusionMatrix",
    components: {Col3, Row, ConfusionMatrixItems, Modal, BarChart, DataTable, MyBoxesModal},
    data () {
      return {
        confidenceThreshold: 70,
        confusedThreshold: 50,
        backgroundThreshold: 50,
        fitThreshold: 90,
        pointDistance: 0.02,
        showModal: false,
        modalContent: [],
        modalContentType: false,
        modalContentModelId: false,
        show_boxes_modal: false,
        boxes_modal_label_name: false,
        local_model_results: [],
      }
    },
    props: {
      model_results: {required: true},
    },
    watch: {
      model_results: {
        immediate: true,
        deep: true,
        handler(value) {
          this.local_model_results = Array.isArray(value)
            ? JSON.parse(JSON.stringify(value))
            : [];
        },
      },
    },
    methods: {
      preview_label_in_modal(label_name) {
        this.show_boxes_modal = true;
        this.boxes_modal_label_name = label_name;
      },
      close_preview_label_in_modal() {
        this.show_boxes_modal = false;
        this.boxes_modal_label_name = null;
      },
      openModal(content, modalContentType, modalContentModelId) {
        if(this.local_model_results.length > 0 && modalContentModelId === 1){
          modalContentModelId = this.local_model_results[0].model_id;
        }
        if(this.local_model_results.length > 1 && modalContentModelId === 2){
          modalContentModelId = this.local_model_results[1].model_id;
        }

        this.modalContentType = modalContentType
        this.modalContentModelId = modalContentModelId
        this.modalContent = Array.isArray(content) ? content.slice() : []
        this.showModal = true
      },
      closeModal() {
        this.showModal = false
      },
      onPredictionAccepted(payload) {
        const response = payload && payload.response;
        const bboxId = payload && payload.bbox_id;
        if (bboxId != null) {
          this.modalContent = this.modalContent.filter((item) => item.bbox_id !== bboxId);
        }
        if (response && Array.isArray(response.items) && Array.isArray(response.labels)) {
          this.applyUpdatedConfMatrix(response);
        }
        this.$emit('prediction_accepted', payload);
      },
      applyUpdatedConfMatrix(response) {
        const modelId = response.model_used || this.modalContentModelId;
        const labels = Object.assign({}, ...response.labels.map((x) => ({[x.id]: x})));
        const resultPerLabel = {};
        for (let i = 0; i < response.items.length; i++) {
          const item = response.items[i];
          const label = labels[item.label_id];
          if (label) {
            resultPerLabel[label.name] = item;
          }
        }
        const idx = this.local_model_results.findIndex((m) => m.model_id === modelId);
        if (idx === -1) {
          return;
        }
        const next = JSON.parse(JSON.stringify(this.local_model_results));
        next[idx] = {
          ...next[idx],
          results: resultPerLabel,
        };
        this.local_model_results = next;
        this.$emit('model_results_updated', next);
      },
      ucfirst(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
      },
      add_headers_for_models(text, id, models, items) {
        for(let i=0; i<models.length; i++) {
          let label = this.ucfirst(text) + ' ' + (i + 1)
          let id_label = id + (i + 1).toString()
          items.push(
            { text: label, value: id_label },)
        }
        return items;
      },
      entryHasFit(item) {
        return item != null && (item.iou != null || item.dist != null);
      },
      entryIsTight(item) {
        if (item.iou != null) {
          return Number(item.iou) >= this.fitThreshold / 100;
        }
        if (item.dist != null) {
          return Number(item.dist) <= Number(this.pointDistance);
        }
        return false;
      },
      modelFitState(modelResult) {
        const state = { hasFit: false, hasUnscored: false };
        const results = (modelResult && modelResult.results) || {};
        const labelNames = Object.keys(results);
        for (let i = 0; i < labelNames.length; i++) {
          const raw = results[labelNames[i]].correct;
          if (!raw) {
            continue;
          }
          let entries;
          try {
            entries = JSON.parse(raw);
          } catch (e) {
            continue;
          }
          if (!Array.isArray(entries)) {
            continue;
          }
          for (let j = 0; j < entries.length; j++) {
            if (this.entryHasFit(entries[j])) {
              state.hasFit = true;
            } else {
              state.hasUnscored = true;
            }
          }
        }
        return state;
      },
      spatialFilterForModel(fitMode, state) {
        if (fitMode === 'tight' || fitMode === 'loose') {
          if (!state.hasFit) {
            return { applies: false, filter: null };
          }
          return { applies: true, filter: fitMode };
        }
        if (fitMode === 'legacy') {
          if (state.hasFit && state.hasUnscored) {
            return { applies: true, filter: 'unscored' };
          }
          if (state.hasFit) {
            return { applies: false, filter: null };
          }
          return { applies: true, filter: null };
        }
        return { applies: true, filter: null };
      },
      add_values_to_row(result_key, output_result_key, model_results, label_name, row, percentage, threshold, lower_than_threshold, fitMode) {
        for(let i=0; i<model_results.length; i++) {
          let id_label = output_result_key + (i + 1).toString()
          const state = this.fitState[i] || { hasFit: false, hasUnscored: false };
          const spatial = this.spatialFilterForModel(fitMode, state);
          if (!spatial.applies) {
            row[id_label] = null;
            row[id_label + '_items'] = [];
            continue;
          }

          if(-1 === Object.keys(model_results[i]['results']).indexOf(label_name)) {
            row[id_label] = 0;
          }
          else {
            let items = model_results[i]['results'][label_name][result_key];

            if (result_key === 'total') {
              row[id_label] = items
            } else if (items) {
              items = JSON.parse(items)
              if(threshold) {
                if(lower_than_threshold) {
                  items = items.filter(item=>{return item.conf < threshold})
                }
                else {
                  items = items.filter(item=>{return item.conf >= threshold})
                }

              }
              if (spatial.filter === 'tight') {
                items = items.filter(item => this.entryIsTight(item));
              } else if (spatial.filter === 'loose') {
                items = items.filter(item => this.entryHasFit(item) && !this.entryIsTight(item));
              } else if (spatial.filter === 'unscored') {
                items = items.filter(item => !this.entryHasFit(item));
              }

              if(percentage) {
                row[id_label] = items.length / row['total' + (i+1)] * 100;
              }
              else {
                row[id_label] = items.length;
              }

              row[id_label + '_items'] = items;
            } else if (items === '') {
              row[id_label] = 0;
            }
          }
        }
        return row
      },
      add_row_for_models(label_name, model_results, items) {
        let row = {}
        row['label'] = label_name
        row = this.add_values_to_row('total', 'total', model_results, label_name, row)
        if (this.showFitColumns) {
          row = this.add_values_to_row('correct', 'correct_tight', model_results, label_name, row, true, this.confidenceThreshold, false, 'tight')
          row = this.add_values_to_row('correct', 'correct_loose', model_results, label_name, row, true, this.confidenceThreshold, false, 'loose')
        }
        if (this.showLegacyCorrect) {
          row = this.add_values_to_row('correct', 'correct', model_results, label_name, row, true, this.confidenceThreshold, false, 'legacy')
        }
        row = this.add_values_to_row('correct', 'correct_low',model_results, label_name, row, true, this.confidenceThreshold, true)
        row = this.add_values_to_row('confused_with','confused_with',
            model_results, label_name, row, false,
            this.confusedThreshold, false)
        row = this.add_values_to_row('confused_with', 'confused_with_low',
            model_results, label_name, row, false,
            this.confusedThreshold, true)
        row = this.add_values_to_row('confused_with_background','confused_with_background',
            model_results, label_name, row, false,
            this.backgroundThreshold, false)
        row = this.add_values_to_row('confused_with_background', 'confused_with_background_low',
            model_results, label_name, row, false,
            this.backgroundThreshold, true)
        row = this.add_values_to_row( 'missed', 'missed',model_results, label_name, row)
        items.push(row);
        return items;
      }
    },
    computed: {
      model_labels() {
        let labels = [];
        for(let i=0; i<this.local_model_results.length; i++) {
          let model_result = this.local_model_results[i];
          let result_labels = Object.keys(model_result.results);
          result_labels.forEach(label_name => {
            labels.push(label_name);
          });
        }
        return [...new Set(labels)];
      },
      fitState() {
        return this.local_model_results.map((model) => this.modelFitState(model));
      },
      showFitColumns() {
        return this.fitState.some((state) => state.hasFit);
      },
      showLegacyCorrect() {
        return this.fitState.some((state) => !state.hasFit || state.hasUnscored);
      },
      items() {
        let items = []
        this.model_labels.forEach(label_name=>{
          this.add_row_for_models(label_name, this.local_model_results, items)
        })
        return items;
      },
      headers() {
        let items = []
        items.push({ text: 'Label', value: 'label' });
        items = this.add_headers_for_models('Total', 'total', this.local_model_results, items)
        if (this.showFitColumns) {
          items = this.add_headers_for_models('Correct (super accurate)', 'correct_tight', this.local_model_results, items)
          items = this.add_headers_for_models('Correct (less accurate)', 'correct_loose', this.local_model_results, items)
        }
        if (this.showLegacyCorrect) {
          items = this.add_headers_for_models('Correct', 'correct', this.local_model_results, items)
        }
        items = this.add_headers_for_models('Correct low', 'correct_low', this.local_model_results, items)
        items = this.add_headers_for_models('Confused', 'confused_with', this.local_model_results, items)
        items = this.add_headers_for_models('Confused low', 'confused_with_low', this.local_model_results, items)
        items = this.add_headers_for_models('Missed', 'missed', this.local_model_results, items)
        items = this.add_headers_for_models('False positive', 'confused_with_background', this.local_model_results, items)
        items = this.add_headers_for_models('False positive low', 'confused_with_background_low', this.local_model_results, items)
        return items;
      },
    }
  }
</script>

<style scoped>

</style>